import { useCallback } from 'react';
import { formatPriceAsCents } from '@moda/portal-stanchions';
import waitUntil from 'wait-until-promise';
import { UseUserQuery } from '../../../generated/types';
import { useUser } from '../../useUser';
import { useCart, CheckoutCart, CartVersion } from '../../useCart';
import { useAddressesQuery, RestAddress } from '../../useAddress';
import { usePlaceAffirmOrderMutation } from './usePlaceAffirmOrderMutation';

type User = NonNullable<UseUserQuery['user']>;

const TIMEOUT_MS = 1800000;
const INTERVAL_MS_CHECK_SHOWING = 500;
const INTERVAL_MS_NOT_SHOWING = 2000;

type AffirmCheckoutArgs = {
  user: User;
  cart: CheckoutCart;
  shippingAddress: RestAddress;
  discounts?: Record<string, Record<string, unknown>> | null;
};

export type AffirmCheckoutResponse = {
  billing_address: {
    city: string;
    line1: string;
    line2: string;
    state: string;
    zipcode: string;
  };
  callback_id: string;
  cardholder_name: string;
  checkout_token: string;
  created: string;
  cvv: string;
  expiration: string;
  id: string;
  number: string;
};

const getAffirmCheckoutData = ({ user, cart, shippingAddress, discounts }: AffirmCheckoutArgs) => ({
  shipping: {
    name: {
      first: shippingAddress.attributes.firstName,
      last: shippingAddress.attributes.lastName
    },
    address: {
      line1: shippingAddress.attributes.addressLine1,
      line2: shippingAddress.attributes.addressLine2,
      city: shippingAddress.attributes.city,
      state: shippingAddress.attributes.state,
      zipcode: shippingAddress.attributes.postalCode,
      country: shippingAddress.attributes.countryCode
    },
    phone_number: shippingAddress.attributes.phone,
    email: user.email
  },
  items: cart.attributes.cartItems.map(cartItem => ({
    display_name: cartItem.data.attributes.productName,
    sku: cartItem.data.attributes.skuId,
    unit_price: formatPriceAsCents(cartItem.data.attributes.prices.currentPrice.price),
    qty: cartItem.data.attributes.quantity,
    item_image_url: cartItem.data.attributes.imageSrc,
    item_url: window.location.origin + cartItem.data.attributes.variantHref
  })),
  shipping_amount: formatPriceAsCents(cart.attributes.paymentSummary.totalShipping.price),
  tax_amount: formatPriceAsCents(cart.attributes.paymentSummary.totalTaxes.price),
  total: formatPriceAsCents(cart.attributes.paymentSummary.cardPaymentToday.price),
  metadata: {
    mode: 'modal'
  },
  discounts
});

export const useAffirmCheckout = () => {
  const { cart } = useCart();
  const { user } = useUser();
  const { addresses } = useAddressesQuery();
  const [placeOrder] = usePlaceAffirmOrderMutation();
  const shippingAddress = addresses.find(
    address => Number(address.id) === cart.attributes.addressId
  );

  const placeAffirmOrder = useCallback((): ReturnType<typeof placeOrder> => {
    if (cart.attributes.version !== CartVersion.Checkout)
      return Promise.reject('placeAffirmOrder: cart version is not checkout');
    if (!shippingAddress) return Promise.reject('placeAffirmOrder: no shipping address selected');

    let discounts: Record<string, unknown> | null = {};
    if (cart.attributes.userPromotions && cart.attributes.userPromotions.length > 0) {
      const { isValid, name, offerCode } = cart.attributes.userPromotions[0];
      const discountAmount = cart.attributes.paymentSummary.promotionDiscount.price;
      discounts['discounts'] = {
        [offerCode]: { ['discount_amount']: discountAmount, ['discount_display_name']: name }
      };
      discounts = isValid ? discounts : null;
    }

    return new Promise((resolve, reject) => {
      window.affirm.checkout.open_vcn({
        checkout_data: getAffirmCheckoutData({
          user,
          cart: cart as CheckoutCart,
          shippingAddress,
          ...discounts
        }),
        success: async (response: AffirmCheckoutResponse) => {
          try {
            const result = await placeOrder(response);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        },
        error: (error: Error) => {
          reject(error);
        }
      });

      waitUntil(
        () => document.getElementById('affirm-error-modal') !== null,
        TIMEOUT_MS,
        INTERVAL_MS_CHECK_SHOWING
      ).then(() => {
        waitUntil(
          () => document.getElementById('affirm-error-modal') === null,
          TIMEOUT_MS,
          INTERVAL_MS_NOT_SHOWING
        ).then(() => {
          reject();
        });
      });
    });
  }, [cart, shippingAddress, user, placeOrder]);

  return { placeAffirmOrder };
};
